import { useCallback, useEffect } from 'react';
import { useGridStore } from '@/stores/gridStore';

interface UseGridKeyboardOptions {
  enabled?: boolean;
  onUndo?: (changes?: unknown[]) => void;
  onRedo?: (changes?: unknown[]) => void;
  onDelete?: (taskIds?: string[]) => void;
  onCopy?: () => void;
  onPaste?: () => void;
  onSelectAll?: () => void;
  onEscape?: () => void;
  rowCount?: number;
  columnCount?: number;
}

export function useGridKeyboard({
  enabled = true,
  onUndo,
  onRedo,
  onDelete,
  onCopy,
  onPaste,
  onSelectAll,
  onEscape,
  rowCount = 0,
  columnCount = 0,
}: UseGridKeyboardOptions) {
  const {
    selectedCell,
    editingCell,
    selectedRows,
    selectCell,
    startEditing,
    stopEditing,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useGridStore();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      // Don't handle if we're in an input or textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // But still handle Escape to exit editing
        if (e.key === 'Escape' && editingCell) {
          stopEditing();
        }
        return;
      }

      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      // Ctrl/Cmd + Z: Undo
      if (isCtrlOrCmd && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo()) {
          const changes = undo();
          if (changes && onUndo) {
            onUndo(changes);
          }
        }
        return;
      }

      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y: Redo
      if (
        (isCtrlOrCmd && e.key === 'z' && e.shiftKey) ||
        (isCtrlOrCmd && e.key === 'y')
      ) {
        e.preventDefault();
        if (canRedo()) {
          const changes = redo();
          if (changes && onRedo) {
            onRedo(changes);
          }
        }
        return;
      }

      // Ctrl/Cmd + C: Copy
      if (isCtrlOrCmd && e.key === 'c') {
        if (onCopy) {
          e.preventDefault();
          onCopy();
          return;
        }
      }

      // Ctrl/Cmd + V: Paste
      if (isCtrlOrCmd && e.key === 'v') {
        if (onPaste) {
          e.preventDefault();
          onPaste();
          return;
        }
      }

      // Ctrl/Cmd + A: Select all
      if (isCtrlOrCmd && e.key === 'a') {
        e.preventDefault();
        onSelectAll?.();
        return;
      }

      // Delete/Backspace: Delete selected rows
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!editingCell && selectedRows.size > 0) {
          e.preventDefault();
          onDelete?.(Array.from(selectedRows));
          return;
        }
      }

      // Don't handle navigation if editing
      if (editingCell) {
        return;
      }

      // Navigation keys
      if (!selectedCell) return;

      const { rowIndex, columnIndex } = selectedCell;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (rowIndex > 0) {
            selectCell({ rowIndex: rowIndex - 1, columnIndex });
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (rowIndex < rowCount - 1) {
            selectCell({ rowIndex: rowIndex + 1, columnIndex });
          }
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (columnIndex > 0) {
            selectCell({ rowIndex, columnIndex: columnIndex - 1 });
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (columnIndex < columnCount - 1) {
            selectCell({ rowIndex, columnIndex: columnIndex + 1 });
          }
          break;

        case 'Tab':
          e.preventDefault();
          if (e.shiftKey) {
            // Move left, wrap to previous row
            if (columnIndex > 0) {
              selectCell({ rowIndex, columnIndex: columnIndex - 1 });
            } else if (rowIndex > 0) {
              selectCell({
                rowIndex: rowIndex - 1,
                columnIndex: columnCount - 1,
              });
            }
          } else {
            // Move right, wrap to next row
            if (columnIndex < columnCount - 1) {
              selectCell({ rowIndex, columnIndex: columnIndex + 1 });
            } else if (rowIndex < rowCount - 1) {
              selectCell({ rowIndex: rowIndex + 1, columnIndex: 0 });
            }
          }
          break;

        case 'Enter':
          e.preventDefault();
          // Enter edit mode - the DataGrid component will need to map
          // selectedCell to actual taskId and columnId
          break;

        case 'Escape':
          e.preventDefault();
          selectCell(null);
          onEscape?.();
          break;

        case 'Home':
          e.preventDefault();
          if (isCtrlOrCmd) {
            // Go to first cell
            selectCell({ rowIndex: 0, columnIndex: 0 });
          } else {
            // Go to first column in current row
            selectCell({ rowIndex, columnIndex: 0 });
          }
          break;

        case 'End':
          e.preventDefault();
          if (isCtrlOrCmd) {
            // Go to last cell
            selectCell({
              rowIndex: rowCount - 1,
              columnIndex: columnCount - 1,
            });
          } else {
            // Go to last column in current row
            selectCell({ rowIndex, columnIndex: columnCount - 1 });
          }
          break;

        case 'PageUp':
          e.preventDefault();
          selectCell({
            rowIndex: Math.max(0, rowIndex - 10),
            columnIndex,
          });
          break;

        case 'PageDown':
          e.preventDefault();
          selectCell({
            rowIndex: Math.min(rowCount - 1, rowIndex + 10),
            columnIndex,
          });
          break;
      }
    },
    [
      enabled,
      editingCell,
      selectedCell,
      selectedRows,
      selectCell,
      stopEditing,
      undo,
      redo,
      canUndo,
      canRedo,
      onUndo,
      onRedo,
      onDelete,
      onCopy,
      onPaste,
      onSelectAll,
      onEscape,
      rowCount,
      columnCount,
    ]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return {
    selectedCell,
    editingCell,
    selectCell,
    startEditing,
    stopEditing,
  };
}

export default useGridKeyboard;
